/**
 * 파일 첨부 (.attachfield) — 여러 파일을 골라 목록으로 보여 주고 하나씩 뺀다.
 *
 * input[type=file] 은 고른 파일을 낱개로 지우지 못하므로 목록은 여기서 들고 있다가
 * DataTransfer 로 input.files 에 다시 넣는다. 그래서 폼 전송(FormData)은 input 만 보면 된다.
 * 파일 선택 창에서 다시 고르거나 끌어다 놓으면 기존 목록 뒤에 더해진다(덮어쓰지 않는다).
 *
 * 한도(data-max-files · data-max-bytes)를 넘는 파일은 빼고 root 에서 attachfield:error 를
 * 띄운다(detail.message, 버블링). 안내 자리는 화면마다 달라 여기서 그리지 않는다.
 * 이미지는 object URL 로 작은 미리보기를 붙이고 뺄 때 revoke 한다.
 * 폼이 reset 되면 목록도 비운다.
 */

import { guardWindowDrop } from './dropGuard';

const MB = 1048576;

const formatSize = (bytes) =>
  bytes >= MB ? `${(bytes / MB).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;

const sameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const InitAttachField = () => {
  const roots = document.querySelectorAll('[data-attachfield]');

  if (0 === roots.length) {
    return;
  }

  guardWindowDrop('.attachfield');

  roots.forEach((root) => {
    const input = root.querySelector('.attachfield__input');
    const zone = root.querySelector('[data-attachfield-zone]');
    const list = root.querySelector('[data-attachfield-list]');
    const empty = root.querySelector('[data-attachfield-empty]');
    const count = root.querySelector('[data-attachfield-count]');

    if (!input || !zone || !list) {
      return;
    }

    const maxFiles = Number(root.dataset.maxFiles) || 0;
    const maxBytes = Number(root.dataset.maxBytes) || 0;
    const maxMb = maxBytes ? Math.max(1, Math.floor(maxBytes / MB)) : 0;

    /** @type {{ file: File, url: string }[]} */
    let items = [];

    const fail = (message) =>
      root.dispatchEvent(
        new CustomEvent('attachfield:error', { bubbles: true, detail: { message } })
      );

    /** 목록을 input.files 에 그대로 옮긴다 (프로그램으로 넣으면 change 는 나지 않는다) */
    const sync = () => {
      const transfer = new DataTransfer();
      items.forEach((item) => transfer.items.add(item.file));
      input.files = transfer.files;
    };

    const renderItem = ({ file, url }, index) => {
      const item = document.createElement('li');
      item.className = 'attachfield__item';

      const thumb = document.createElement('span');
      thumb.className = url ? 'attachfield__thumb' : 'attachfield__thumb attachfield__thumb--file';

      if (url) {
        const image = document.createElement('img');
        image.src = url;
        image.alt = '';
        thumb.append(image);
      }

      const name = document.createElement('span');
      name.className = 'attachfield__name';
      name.textContent = file.name;
      name.title = file.name;

      const size = document.createElement('span');
      size.className = 'attachfield__size';
      size.textContent = formatSize(file.size);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'attachfield__remove';
      remove.dataset.attachfieldRemove = String(index);
      remove.setAttribute('aria-label', `${file.name} 빼기`);

      item.append(thumb, name, size, remove);

      return item;
    };

    const render = () => {
      list.replaceChildren(...items.map(renderItem));
      list.hidden = 0 === items.length;

      if (empty) {
        empty.hidden = 0 < items.length;
      }

      if (count) {
        count.textContent = maxFiles ? `${items.length}/${maxFiles}` : String(items.length);
      }
    };

    /** @param {FileList | File[] | null | undefined} files */
    const add = (files) => {
      const problems = [];

      Array.from(files || []).forEach((file) => {
        if (items.some((item) => sameFile(item.file, file))) {
          return;
        }

        if (maxFiles && items.length >= maxFiles) {
          problems.push(`첨부파일은 최대 ${maxFiles}개까지 등록할 수 있습니다.`);
          return;
        }

        if (maxBytes && file.size > maxBytes) {
          problems.push(
            `"${file.name}" 파일이 너무 큽니다. 파일당 ${maxMb}MB 이하만 등록할 수 있습니다.`
          );
          return;
        }

        items.push({ file, url: file.type.startsWith('image/') ? URL.createObjectURL(file) : '' });
      });

      sync();
      render();

      if (problems.length) {
        fail(problems[0]);
      }
    };

    const removeAt = (index) => {
      const [gone] = items.splice(index, 1);

      if (gone?.url) {
        URL.revokeObjectURL(gone.url);
      }

      sync();
      render();
    };

    const clear = () => {
      items.forEach((item) => item.url && URL.revokeObjectURL(item.url));
      items = [];
      sync();
      render();
    };

    // 파일 선택 창 — 고른 것을 기존 목록 뒤에 더한다
    input.addEventListener('change', () => add(input.files));

    list.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-attachfield-remove]');

      if (button) {
        removeAt(Number(button.dataset.attachfieldRemove));
      }
    });

    const over = (event) => {
      event.preventDefault();
      zone.classList.add('attachfield__zone--over');
    };

    const leave = () => zone.classList.remove('attachfield__zone--over');

    zone.addEventListener('dragenter', over);
    zone.addEventListener('dragover', over);
    zone.addEventListener('dragleave', leave);
    zone.addEventListener('drop', (event) => {
      event.preventDefault();
      leave();
      add(event.dataTransfer?.files);
    });

    // 폼을 비우면(접수 성공 뒤 form.reset()) 목록도 비운다
    root.closest('form')?.addEventListener('reset', clear);

    render();
  });
};

export default InitAttachField;
